import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react'
import Chartjs, { ChartTooltipModelBody, ChartTooltipItem, ChartTooltipModel, ChartData, ChartDataSets } from 'chart.js'
import { getUniqueListBy } from '../utils/array'

import _orderBy from 'lodash/orderBy'
// import Chart from 'chart.js'

// const fontFamily = 'Sarabun'

type ISpiderChartView = {
    title?: string
    subtitle?: string
    chartRef: React.MutableRefObject<HTMLCanvasElement | null>
    positionTitle?: 'top' | 'left' | 'bottom' | 'right'
    titleDisplay?: boolean
} & TReturnTypeHooks

export type CallbackEventClickChart = {
    datasetIndex: number
    pointIndex: number
}

export interface ISpiderChart {
    tooltipDisplay?: boolean
    labelsCustomTooltips?: string[]
    labels?: string[]
    datasets?: ChartDataSets[]
    title?: string
    subtitle?: string
    positionTitle?: 'top' | 'left' | 'bottom' | 'right'
    positionLegend?: 'top' | 'left' | 'bottom' | 'right'
    legendDisplay?: boolean
    titleDisplay?: boolean
    tickSuggestedMin?: number
    tickSuggestedMax?: number
    tickStepSize?: number
    tickDisplay?: boolean
    pointLabelFontColor?: string[] | string
    filterLayerIndex?: number
    pointLabelDisplay?: boolean
    datasetLabelDisplay?: boolean
    onClickChart?: (values: CallbackEventClickChart) => void
    fontFamily?: 'THSarabunNew' | 'Prompt' | 'Sarabun'
}

type TReturnTypeHooks = ReturnType<typeof useSpiderChart>
const useSpiderChart = ({
    labels = [],
    labelsCustomTooltips = [],
    datasets,
    positionLegend = 'right',
    legendDisplay = true,
    tickSuggestedMin = 0,
    tickSuggestedMax = 4,
    title,
    subtitle,
    positionTitle = 'top',
    titleDisplay = false,
    tickStepSize = 1,
    pointLabelFontColor = '#000000',
    tickDisplay = true,
    filterLayerIndex = 0,
    pointLabelDisplay = true,
    datasetLabelDisplay = false,
    tooltipDisplay = true,
    onClickChart,
    fontFamily = 'Prompt',
}: ISpiderChart) => {
    const [chartInstance, setChartInstance] = useState<Chartjs | null>(null)
    const chartRef = useRef<HTMLCanvasElement | null>(null)
    const myChart = useRef<Chartjs | null>(null)

    const chartConfig = useMemo(() => {
        const fontSizeNormal = fontFamily === 'THSarabunNew' ? 20 : 14
        const fontSizeToolTip = fontFamily === 'THSarabunNew' ? 16 : 14
        return {
            type: 'radar',
            data: {
                labels,
                datasets,
            },
            pointDot: false,
            options: {
                //@ts-ignore
                onClick: (event, element) => {
                    let tooltipEl = document.getElementById('chartjs-tooltip')
                    if (tooltipEl) {
                        tooltipEl.remove()
                    }

                    if (
                        onClickChart &&
                        element[0]?._index !== undefined &&
                        element[0]?._datasetIndex !== undefined &&
                        element[0]?._datasetIndex === 0
                    ) {
                        onClickChart({
                            datasetIndex: element[0]?._datasetIndex,
                            pointIndex: element[0]?._index,
                        })
                    }
                },
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 8,
                        bottom: 8,
                    },
                },
                title: {
                    display: titleDisplay,
                    position: positionTitle,
                    text: [title, subtitle],
                    // fontSize: 14,
                    fontSize: fontSizeNormal,
                    fontFamily: fontFamily,
                },
                animation: {
                    duration: 0, // general animation time
                    onComplete: function () {
                        //@ts-ignore
                        let chartInstance = this.chart
                        let ctx = chartInstance.ctx

                        ctx.font = `12px ${fontFamily}`
                        ctx.fontStyle = 'bold'
                        ctx.fillStyle = '#000000'
                        ctx.textAlign = 'center'
                        ctx.textBaseline = 'bottom'

                        datasets?.forEach?.((dataset: ChartDataSets, indexDataset: number) => {
                            //@ts-ignore
                            let meta = chartInstance.controller.getDatasetMeta(indexDataset)

                            //@ts-ignore
                            meta.data.forEach((bar, index) => {
                                let data: number | number[] | Chartjs.ChartPoint | null | undefined =
                                    dataset && dataset?.data && dataset.data[index]

                                if (!meta.hidden && data && indexDataset > 0 && datasetLabelDisplay) {
                                    ctx.fillText(data, bar._model.x, bar._model.y - 5)
                                }
                            })
                        })
                    },
                },
                hover: {
                    animationDuration: 0, // duration of animations when hovering an item
                },
                responsiveAnimationDuration: 0, // animation duration after a resize
                responsive: true,
                // maintainAspectRatio: true,
                maintainAspectRatio: false,
                tooltips: {
                    enabled: false,
                    backgroundColor: '#FFF',
                    titleFontSize: fontSizeNormal,
                    titleFontColor: '#000',
                    bodyFontColor: '#000',
                    bodyFontSize: fontSizeNormal,
                    bodySpacing: 5,
                    titleFontFamily: fontFamily,
                    bodyFontFamily: fontFamily,
                    borderColor: '#b7b7b7',
                    borderWidth: 0.5,
                    filter: function (item: ChartTooltipItem, data: ChartData) {
                        if (!tooltipDisplay) return null
                        if (item.datasetIndex) {
                            //@ts-ignore
                            const dataSum: number = data.datasets[item?.datasetIndex]?.data?.reduce(
                                (sum: number, number: number) => {
                                    return sum + number
                                },
                                0,
                            )
                            return dataSum > 0 && item?.datasetIndex > filterLayerIndex
                        }
                    },
                    callbacks: {
                        title: (tooltipItem: any[], data: any) => {
                            if (labelsCustomTooltips.length && tooltipItem && tooltipItem.length > 0) {
                                return tooltipItem.map((tooltip) => {
                                    return {
                                        datasetIndex: tooltip.datasetIndex,
                                        index: tooltip.index,
                                        title: labelsCustomTooltips[tooltip.index],
                                        ...data.datasets[tooltip.datasetIndex],
                                    }
                                })
                            }
                            if (tooltipItem && tooltipItem.length > 0 && data.labels?.length) {
                                return tooltipItem.map((tooltip) => {
                                    return {
                                        datasetIndex: tooltip.datasetIndex,
                                        index: tooltip.index,
                                        title: data.labels[tooltip.index],
                                        ...data.datasets[tooltip.datasetIndex],
                                    }
                                })
                            }
                        },
                        label: function (tooltipItem: any, data: any) {
                            //@ts-ignore
                            let datasetLabel: string = data?.datasets[tooltipItem.datasetIndex]?.label || ''
                            //This will be the tooltip.body
                            // return datasetLabel + ': <span style="font-weight:bold;">' + tooltipItem.yLabel + '</span>'
                            return {
                                datasetIndex: tooltipItem.datasetIndex,
                                index: tooltipItem.index,
                                body:
                                    datasetLabel +
                                    ': <span style="font-weight:bold;">' +
                                    parseFloat(tooltipItem.yLabel).toFixed(2) +
                                    '</span>',
                                ...data.datasets[tooltipItem.datasetIndex],
                            }
                        },
                    },
                    custom: function (tooltipModel: ChartTooltipModel, arg2: any) {
                        let tooltipEl: HTMLElement | null = document.getElementById('chartjs-tooltip')

                        if (tooltipModel.body && tooltipModel.body.length > 0) {
                            // Tooltip Element

                            // Create element on first render
                            if (!tooltipEl) {
                                tooltipEl = document.createElement('div')
                                tooltipEl.id = 'chartjs-tooltip'
                                tooltipEl.innerHTML = '<table></table>'
                                document.body.appendChild(tooltipEl)
                            }

                            // Hide if no tooltip
                            if (tooltipModel.opacity === 0) {
                                //@ts-ignore
                                tooltipEl.style.opacity = 0
                                return
                            }

                            // Set caret Position
                            tooltipEl.classList.remove('above', 'below', 'no-transform')

                            if (tooltipModel.yAlign) {
                                tooltipEl.classList.add(tooltipModel.yAlign)
                            } else {
                                tooltipEl.classList.add('no-transform')
                            }

                            function getBody(bodyItem: ChartTooltipModelBody) {
                                return bodyItem.lines
                            }

                            // Set Text
                            if (tooltipModel.body) {
                                let titleLines = tooltipModel.title || []
                                let bodyLines = tooltipModel.body.map(getBody)

                                let innerHtml = ''

                                const groupBody = _orderBy(getUniqueListBy(titleLines, 'index'), 'index', 'asc').map(
                                    (titleItem) => {
                                        const groupBodyLines = bodyLines?.filter((bodyItem: any[], index) =>
                                            bodyItem.some((body: any) => body.index === titleItem?.index),
                                        )

                                        return {
                                            index: titleItem.index,
                                            title: titleItem.label,
                                            body: groupBodyLines,
                                            ...titleItem,
                                        }
                                    },
                                )

                                groupBody.forEach((groupBodyItem: any, indexGroup: number) => {
                                    innerHtml +=
                                        '<tr ><th style="text-align:left;width:400px;word-break:break-word;">' +
                                        groupBodyItem.title +
                                        '</th></tr>'
                                    groupBodyItem.body.forEach((bodyItem: any, indexBody: number) => {
                                        //@ts-ignore
                                        let borderStyle = datasets[bodyItem[0].datasetIndex]?.borderDash
                                            ? 'dashed'
                                            : 'solid'

                                        let borderColorLegend =
                                            typeof bodyItem[0].pointBackgroundColor === 'string'
                                                ? bodyItem[0].pointBackgroundColor
                                                : bodyItem[0].pointBackgroundColor[bodyItem[0].index]

                                        let borderColor = borderColorLegend ?? bodyItem[0].borderColor

                                        //@ts-ignore
                                        let style = 'background-color:transparent'
                                        //@ts-ignore
                                        style += '; border-color:' + borderColor
                                        style += '; border-style:' + borderStyle
                                        style += '; border-width: 2px'
                                        style += '; margin: 5px'
                                        style += '; width: 15px'
                                        style += '; height: 5px'
                                        style += '; display: table-cell'
                                        style += '; padding:  0px 10px;'

                                        let bodyCustom =
                                            '<div style="padding: 0px 10px; display: table-cell;">' +
                                            bodyItem[0].body +
                                            '</div>'

                                        let span = '<div class="chartjs-tooltip-key" style="' + style + '">&nbsp</div>'
                                        innerHtml +=
                                            '<tr><td><div style="margin-bottom:5px;display: table-row;">' +
                                            span +
                                            bodyCustom +
                                            '</div></td></tr>'
                                    })
                                })

                                let tableRoot: HTMLTableElement | null = tooltipEl.querySelector('table')
                                if (tableRoot) tableRoot.innerHTML = innerHtml
                            }

                            // `this` will be the overall tooltip
                            //@ts-ignore
                            let position = this._chart.canvas.getBoundingClientRect()

                            // Display, position, and set styles for font
                            //@ts-ignore

                            tooltipEl.style.opacity = 1
                            tooltipEl.style.position = 'absolute'
                            tooltipEl.style.zIndex = '2'
                            tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px'
                            tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px'
                            tooltipEl.style.fontSize = fontSizeToolTip + 'px'
                            // tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px'
                            tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle
                            tooltipEl.style.fontFamily = fontFamily
                            tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px'
                            tooltipEl.style.pointerEvents = 'none'
                            tooltipEl.style.backgroundColor = '#fff'
                            tooltipEl.style.borderRadius = '5px'
                            tooltipEl.style.width = 'auto'
                            tooltipEl.style.minWidth = '200px'
                            tooltipEl.style.padding = '10px'
                            tooltipEl.style.boxShadow =
                                '0px 1px 3px -2px rgba(0,0,0,0.2), 0px 1px 3px 0px rgba(0,0,0,0.12), 0px 1px 8px 0px rgba(0,0,0,0.12)'
                        } else {
                            // Hide if no tooltip
                            if (tooltipModel.opacity === 0 && tooltipEl) {
                                //@ts-ignore
                                tooltipEl.style.opacity = 0
                                tooltipEl.remove()
                                return
                            }
                        }
                    },
                },
                legend: {
                    display: legendDisplay,
                    position: positionLegend,
                    // onClick: onClickLegend,
                    // onHover: onHoverLegend,
                    // onLeave: onLeaveLegend,
                    labels: {
                        //@ts-ignore
                        filter: function (legendItem: ChartLegendLabelItem, data: ChartData) {
                            //@ts-ignore
                            const dataSum: number = data.datasets[legendItem.datasetIndex]?.data?.reduce(
                                (sum: number, number: number) => {
                                    return sum + number
                                },
                                0,
                            )

                            return dataSum > 0 && legendItem.datasetIndex > filterLayerIndex
                        },
                        fontColor: '#000000',
                        fontSize: fontSizeNormal,
                        fontFamily,
                    },
                },
                scale: {
                    yAxes: [
                        {
                            display: true,
                            gridLines: {
                                display: true,
                            },
                            ticks: {
                                fontSize: fontSizeNormal - 2,
                                display: true,
                                beginAtZero: true,
                            },
                        },
                    ],
                    xAxes: [
                        {
                            gridLines: {
                                display: true,
                            },
                            ticks: {
                                fontSize: fontSizeNormal - 2,
                                // beginAtZero: true,
                            },
                        },
                    ],
                    gridLines: {
                        drawTicks: true,
                        display: true,
                        borderDash: [3],
                        color: '#b7b7b7',
                    },
                    pointLabels: {
                        fontColor: '#464646',
                        fontSize: fontSizeNormal,
                        display: pointLabelDisplay,
                        fontStyle: 'bold',
                        fontFamily,
                    },
                    angleLines: {
                        display: false,
                    },
                    ticks: {
                        display: tickDisplay,
                        stepSize: tickStepSize,
                        suggestedMin: tickSuggestedMin,
                        suggestedMax: tickSuggestedMax,
                    },
                },
            },
        }
    }, [
        labels,
        datasets,
        titleDisplay,
        positionTitle,
        title,
        subtitle,
        fontFamily,
        legendDisplay,
        positionLegend,
        pointLabelDisplay,
        tickDisplay,
        tickStepSize,
        tickSuggestedMin,
        tickSuggestedMax,
        onClickChart,
        datasetLabelDisplay,
        tooltipDisplay,
        filterLayerIndex,
        labelsCustomTooltips,
    ])

    const updateChart = useCallback(() => {
        if (chartInstance) {
            // let test = labels.map((item) => {
            //     console.log('labels', wrapThaiText(item, 10, ''))
            // })
            //@ts-ignore
            chartInstance.options = chartConfig.options
            chartInstance.data.labels = labels
            chartInstance.data.datasets = datasets
            chartInstance.update()
        }
    }, [chartInstance, chartConfig]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        updateChart()
    }, [updateChart])

    useEffect(() => {
        if (chartRef && chartRef.current) {
            const timeout = fontFamily === 'THSarabunNew' ? 1000 : 0

            setTimeout(() => {
                //@ts-ignore
                const newChartInstance = new Chartjs(chartRef.current, chartConfig)
                setChartInstance(newChartInstance)
            }, timeout) // timeout เพื่อรอ load font
        }

        return () => {
            // //@ts-ignore
            // let tooltipEl = document.getElementById('chartjs-tooltip')
            // if (tooltipEl) {
            //     tooltipEl.remove()
            // }
            chartInstance?.destroy()
        }
    }, [chartRef]) // eslint-disable-line react-hooks/exhaustive-deps

    return { chartRef, myChart, chartConfig }
}

const MySpiderChartView: React.FC<ISpiderChartView> = ({
    chartRef,
    chartConfig,
    // title,
    // subtitle,
    // positionTitle = 'start',
    // positionSubtitle = 'start',
}) => {
    //@ts-ignore
    // return <Radar data={chartConfig.data} options={chartConfig.options} />
    return <canvas ref={chartRef} />
}

const MySpiderChart: React.FC<ISpiderChart> = ({ ...props }) => {
    const spiderChart = useSpiderChart(props)
    return <MySpiderChartView {...spiderChart} />
}

export default MySpiderChart
